(() => {
  const closeGroups = (except) => document.querySelectorAll('[data-vivid-nav-group][open]').forEach((group) => {
    if (group !== except) group.open = false;
  });
  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-vivid-nav-group]');
    closeGroups(target);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const group = document.activeElement?.closest('[data-vivid-nav-group][open]');
    if (group && !group.closest('[data-vivid-menu]')) {
      group.open = false;
      group.querySelector('summary')?.focus();
    } else {
      closeGroups();
    }
  });
  document.addEventListener('focusin', (event) => {
    const target = event.target.closest('[data-vivid-nav-group]');
    closeGroups(target);
  });
})();

(() => {
  const tagName = 'vivid-predictive-search';
  if (!window.customElements || window.customElements.get(tagName)) return;

  class VividPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this._generation = 0;
      this._listeners = [];
      this._options = [];
      this._active = -1;
      this._mounted = false;
    }

    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected && !this._mounted) this._connect();
      });
    }

    _connect() {
      const find = (name) => this.querySelector('[data-vivid-search-' + name + ']');
      this._input = find('input');
      this._panel = find('panel');
      this._results = find('results');
      this._list = find('list');
      this._status = find('status');
      this._all = find('view-all');
      this._toggle = find('toggle');
      this._form = this._input && this._input.closest('form');
      if (![this._input, this._panel, this._results, this._list, this._status, this._all, this._toggle, this._form].every(Boolean)) return;
      if (!this._input.id || !this._list.id) return;
      const labels = ['loading', 'empty', 'error', 'results', 'groupServices', 'groupPages', 'groupGuides', 'demo'];
      if (!labels.every((key) => typeof this.dataset[key] === 'string' && this.dataset[key].trim())) return;
      try {
        this._endpoint = new URL(this.dataset.route, window.location.origin);
        this._searchUrl = new URL(this._form.action, window.location.origin);
        if (this._endpoint.origin !== window.location.origin || this._searchUrl.origin !== window.location.origin) return;
        if (!this._endpoint.pathname.endsWith('/search/suggest') || !this._searchUrl.pathname.endsWith('/search')) return;
        this._localePrefix = this._endpoint.pathname.slice(0, -'/search/suggest'.length);
        this._endpoint.pathname += '.json';
        this._endpoint.search = '';
        this._endpoint.hash = '';
      } catch (_) {
        return;
      }
      this._mounted = true;
      this._media = window.matchMedia(this.dataset.mobileQuery || '(max-width: 1099px)');
      this._inputOriginal = {};
      for (const name of ['role', 'aria-autocomplete', 'aria-controls', 'aria-expanded', 'aria-activedescendant']) {
        this._inputOriginal[name] = this._input.getAttribute(name);
      }
      this._input.setAttribute('role', 'combobox');
      this._input.setAttribute('aria-autocomplete', 'list');
      this._input.setAttribute('aria-controls', this._list.id);
      this._input.setAttribute('aria-expanded', 'false');
      this._toggle.hidden = false;
      this._toggle.setAttribute('aria-expanded', 'false');
      this.dataset.searchReady = 'true';
      this._on(this._input, 'input', (event) => {
        if (!event.isComposing && !this._composing) this._schedule();
      });
      this._on(this._input, 'compositionstart', () => {
        this._composing = true;
        this._cancelWork();
        this._hideResults();
      });
      this._on(this._input, 'compositionend', () => {
        this._composing = false;
        this._schedule();
      });
      this._on(this._input, 'focus', () => {
        if (this._suppressFocus) this._suppressFocus = false;
        else if (this._query().length >= 2) this._schedule();
      });
      this._on(this, 'keydown', (event) => this._keydown(event));
      this._on(this._toggle, 'click', () => {
        if (this.dataset.mobileOpen === 'true') this._close(true);
        else {
          this.dataset.mobileOpen = 'true';
          this._toggle.setAttribute('aria-expanded', 'true');
          this._input.focus();
        }
      });
      for (const button of this.querySelectorAll('[data-vivid-search-close]')) {
        this._on(button, 'click', () => this._close(true));
      }
      this._on(document, 'pointerdown', (event) => {
        if (!this.contains(event.target)) this._close(false);
      });
      this._on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this._close(false);
      });
      this._on(this._form, 'submit', () => this._close(false));
      this._on(this._all, 'click', () => this._close(false));
      this._on(this._media, 'change', () => this._close(this.contains(document.activeElement)));
      this._updateAll();
    }

    _on(target, type, callback) {
      target.addEventListener(type, callback);
      this._listeners.push([target, type, callback]);
    }

    _query() {
      return this._input.value.trim();
    }

    _updateAll() {
      const url = new URL(this._searchUrl.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('q', this._query());
      url.searchParams.set('type', 'product,page,article');
      url.searchParams.set('options[prefix]', 'last');
      this._all.href = url.href;
    }

    _cancelWork() {
      this._generation += 1;
      window.clearTimeout(this._timer);
      this._timer = null;
      if (this._controller) this._controller.abort();
      this._controller = null;
    }

    _schedule() {
      this._cancelWork();
      this._updateAll();
      this._hideResults();
      const query = this._query();
      if (query.length < 2 || (this._media.matches && this.dataset.mobileOpen !== 'true')) return;
      const generation = this._generation;
      this._timer = window.setTimeout(() => this._request(query, generation), 280);
    }

    _current(query, generation) {
      return this._mounted && generation === this._generation && query === this._query()
        && (!this._media.matches || this.dataset.mobileOpen === 'true');
    }

    async _request(query, generation) {
      if (!this._current(query, generation)) return;
      const controller = new AbortController();
      this._controller = controller;
      this._status.textContent = this.dataset.loading;
      this._list.setAttribute('aria-busy', 'true');
      const url = new URL(this._endpoint.href);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,page,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      try {
        const response = await fetch(url.href, {
          signal: controller.signal,
          credentials: 'same-origin',
          mode: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Search unavailable');
        const payload = await response.json();
        if (!this._current(query, generation)) return;
        if (!payload || !payload.resources || !payload.resources.results) throw new Error('Invalid search response');
        this._render(this._groups(payload.resources.results), generation);
      } catch (error) {
        if (!this._current(query, generation) || error.name === 'AbortError') return;
        this._hideResults();
        this._status.textContent = this.dataset.error;
      } finally {
        if (this._controller === controller) this._controller = null;
        if (this._current(query, generation)) this._list.setAttribute('aria-busy', 'false');
      }
    }

    _safeResult(item, type) {
      if (!item || typeof item.title !== 'string' || !item.title.trim() || typeof item.url !== 'string') return null;
      try {
        const url = new URL(item.url, window.location.origin);
        if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol) || url.username || url.password) return null;
        const match = url.pathname.match(/^(.*)\/(products|pages|blogs)\/(.+)$/);
        if (!match || (match[1] && match[1] !== this._localePrefix)) return null;
        const resource = { products: 'products', pages: 'pages', articles: 'blogs' }[type];
        if (match[2] !== resource || (type === 'articles' && !match[3].includes('/'))) return null;
        const handle = typeof item.handle === 'string' ? item.handle : match[3].split('/').pop();
        const pathHandle = decodeURIComponent(match[3].split('/').pop());
        const excluded = ['vivid-transportation', 'upwork-portfolio', 'meeting-files'];
        if (excluded.includes(handle) || excluded.includes(pathHandle)) return null;
        const aliases = {
          services: 'services', offerings: 'services',
          contact: 'book-a-project-call', consultations: 'book-a-project-call',
          'book-a-project-call': 'book-a-project-call', work: 'expertise', expertise: 'expertise'
        };
        const prefix = match[1] || this._localePrefix;
        url.pathname = prefix + '/' + resource + '/' + (type === 'pages' && aliases[pathHandle] ? aliases[pathHandle] : match[3]);
        url.search = '';
        url.hash = '';
        return {
          title: item.title.trim(),
          href: url.href,
          demo: type === 'products' && ['signature-weave-bracelet-live-customizer-demo', 'gallery-wall-photo-tile-builder-demo'].includes(pathHandle)
        };
      } catch (_) {
        return null;
      }
    }

    _groups(results) {
      const seen = new Set();
      return [
        ['products', this.dataset.groupServices],
        ['articles', this.dataset.groupGuides],
        ['pages', this.dataset.groupPages]
      ].map(([type, label]) => {
        const items = [];
        for (const raw of Array.isArray(results[type]) ? results[type] : []) {
          const item = this._safeResult(raw, type);
          if (!item || seen.has(item.href)) continue;
          seen.add(item.href);
          items.push(item);
          if (items.length === 3) break;
        }
        return { label, items };
      }).filter((group) => group.items.length);
    }

    _render(groups, generation) {
      this._hideResults();
      for (const group of groups) {
        const wrapper = document.createElement('div');
        wrapper.className = 'v-header-search__group';
        wrapper.setAttribute('role', 'group');
        wrapper.setAttribute('aria-label', group.label);
        const heading = document.createElement('div');
        heading.className = 'v-header-search__group-title';
        heading.setAttribute('aria-hidden', 'true');
        heading.textContent = group.label;
        wrapper.append(heading);
        for (const item of group.items) {
          const anchor = document.createElement('a');
          anchor.className = 'v-header-search__option v-header-search__link';
          anchor.id = this._input.id + '-Suggestion-' + generation + '-' + this._options.length;
          anchor.href = item.href;
          anchor.tabIndex = -1;
          anchor.setAttribute('role', 'option');
          anchor.setAttribute('aria-selected', 'false');
          const title = document.createElement('span');
          title.className = 'v-header-search__title';
          title.textContent = item.title;
          anchor.append(title);
          if (item.demo) {
            const badge = document.createElement('span');
            badge.className = 'v-header-search__badge';
            badge.textContent = this.dataset.demo;
            anchor.append(badge);
          }
          wrapper.append(anchor);
          this._options.push(anchor);
        }
        this._list.append(wrapper);
      }
      if (!this._options.length) {
        this._status.textContent = this.dataset.empty;
        return;
      }
      this._results.hidden = false;
      this._input.setAttribute('aria-expanded', 'true');
      this._status.textContent = this.dataset.results.replace(/\[count\]/g, String(this._options.length));
    }

    _hideResults() {
      this._results.hidden = true;
      this._list.replaceChildren();
      this._list.setAttribute('aria-busy', 'false');
      this._options = [];
      this._active = -1;
      this._input.setAttribute('aria-expanded', 'false');
      this._input.removeAttribute('aria-activedescendant');
      this._status.textContent = '';
    }

    _keydown(event) {
      if (event.isComposing || this._composing || event.keyCode === 229) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this._close(true);
        return;
      }
      if (event.target !== this._input || this._results.hidden || !this._options.length) return;
      if (event.key === 'Enter' && this._active >= 0) {
        event.preventDefault();
        this._options[this._active].click();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      if (this._active < 0) this._active = event.key === 'ArrowDown' ? 0 : this._options.length - 1;
      else this._active = (this._active + (event.key === 'ArrowDown' ? 1 : -1) + this._options.length) % this._options.length;
      this._options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this._active)));
      const option = this._options[this._active];
      this._input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    _close(restoreFocus) {
      if (!this._mounted) return;
      this._cancelWork();
      this._hideResults();
      const mobile = this._media.matches;
      delete this.dataset.mobileOpen;
      this._toggle.setAttribute('aria-expanded', 'false');
      if (!restoreFocus) return;
      if (mobile) this._toggle.focus();
      else if (document.activeElement !== this._input) {
        this._suppressFocus = true;
        this._input.focus();
      }
    }

    disconnectedCallback() {
      this._cancelWork();
      for (const [target, type, callback] of this._listeners) target.removeEventListener(type, callback);
      this._listeners = [];
      if (this._mounted) {
        this._hideResults();
        for (const [name, value] of Object.entries(this._inputOriginal)) {
          if (value === null) this._input.removeAttribute(name);
          else this._input.setAttribute(name, value);
        }
        this._toggle.hidden = true;
        this._toggle.setAttribute('aria-expanded', 'false');
      }
      this._mounted = false;
      this._composing = false;
      this._suppressFocus = false;
      delete this.dataset.searchReady;
      delete this.dataset.mobileOpen;
    }
  }

  window.customElements.define(tagName, VividPredictiveSearch);
})();
